import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Card, Input, Button, Tag, Space, Select, Row, Col,
  Tooltip, Badge, Modal, message, Popconfirm, Progress, Alert, Spin
} from 'antd';
import {
  SearchOutlined, DownloadOutlined, UploadOutlined,
  PlayCircleOutlined, PauseCircleOutlined, EditOutlined,
  EllipsisOutlined, ReloadOutlined, ApiOutlined, CloudOutlined
} from '@ant-design/icons';
import { getCardList, stopCard, resumeCard, batchOperate, MOCK_CARDS } from '../api/yueApi';
import { useAuthStore } from '../store/authStore';

const YUE_KEY = process.env.REACT_APP_YUE_APP_KEY || '';
const IS_REAL_API = !!YUE_KEY && YUE_KEY !== 'YOUR_APP_KEY';

const statusMap: Record<string, { color: string; text: string }> = {
  active:  { color: 'success',    text: '正常'  },
  offline: { color: 'warning',    text: '离线'  },
  stop:    { color: 'error',      text: '停机'  },
  cancel:  { color: 'default',    text: '注销'  },
  testing: { color: 'processing', text: '测试期' },
};
const operatorMap: Record<string, string> = {
  cmcc: '中国移动', cucc: '中国联通', ctcc: '中国电信', global: '全球卡',
};

const CardManage: React.FC = () => {
  const { user } = useAuthStore();
  const [data, setData]               = useState<any[]>([]);
  const [loading, setLoading]         = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [search, setSearch]           = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [operatorFilter, setOperatorFilter] = useState('');
  const [detailVisible, setDetailVisible] = useState(false);
  const [currentCard, setCurrentCard] = useState<any>(null);
  const [pagination, setPagination]   = useState({ current: 1, pageSize: 10, total: 0 });

  const fetchCards = useCallback(async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      if (IS_REAL_API) {
        const res = await getCardList({
          page, pageSize,
          status: statusFilter || undefined,
          iccid: search || undefined,
        });
        const resData = (res as any).data?.data || {};
        const { list = [], total = 0 } = resData;
        // 多租户隔离：非超管只看自己租户的卡
        const filtered = user?.role === 'super_admin' || user?.role === 'admin'
          ? list
          : list.filter((c: any) => c.tenantId === user?.tenantId);
        setData(filtered.map((c: any, i: number) => ({ ...c, key: String(i) })));
        setPagination(p => ({ ...p, current: page, total }));
      } else {
        // 模拟数据兜底 + 多租户过滤
        await new Promise(r => setTimeout(r, 400));
        let filtered = MOCK_CARDS;
        if (user?.role === 'agent' || user?.role === 'client') {
          filtered = filtered.filter(c => c.client === user.tenantName);
        }
        if (statusFilter) filtered = filtered.filter(c => c.status === statusFilter);
        if (search)       filtered = filtered.filter(c => c.iccid.includes(search) || c.msisdn.includes(search) || c.client.includes(search));
        setData(filtered.map((c, i) => ({ ...c, key: String(i) })));
        setPagination(p => ({ ...p, current: page, total: filtered.length }));
      }
    } catch (e: any) {
      message.error('获取卡列表失败：' + (e.message || '未知错误'));
      // 降级到模拟数据
      setData(MOCK_CARDS.slice(0, 10).map((c, i) => ({ ...c, key: String(i) })));
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, operatorFilter, user]);

  useEffect(() => { fetchCards(); }, [fetchCards]);

  const handleStop = async (iccid: string) => {
    try {
      if (IS_REAL_API) await stopCard(iccid);
      setData(prev => prev.map(d => d.iccid === iccid ? { ...d, status: 'stop' } : d));
      message.success(`已停机 ${iccid.slice(-6)}`);
    } catch { message.error('操作失败'); }
  };

  const handleResume = async (iccid: string) => {
    try {
      if (IS_REAL_API) await resumeCard(iccid);
      setData(prev => prev.map(d => d.iccid === iccid ? { ...d, status: 'active' } : d));
      message.success(`已复机 ${iccid.slice(-6)}`);
    } catch { message.error('操作失败'); }
  };

  const handleBatch = async (action: 'stop' | 'resume') => {
    if (!selectedKeys.length) { message.warning('请先选择卡片'); return; }
    const iccids = data.filter(d => selectedKeys.includes(d.key)).map(d => d.iccid);
    try {
      if (IS_REAL_API) await batchOperate(iccids, action);
      setData(prev => prev.map(d =>
        selectedKeys.includes(d.key) ? { ...d, status: action === 'stop' ? 'stop' : 'active' } : d
      ));
      message.success(`已对 ${selectedKeys.length} 张卡执行${action === 'stop' ? '停机' : '复机'}`);
      setSelectedKeys([]);
    } catch { message.error('批量操作失败'); }
  };

  const columns = [
    {
      title: 'ICCID', dataIndex: 'iccid', key: 'iccid', width: 190,
      render: (v: string, r: any) => <a onClick={() => { setCurrentCard(r); setDetailVisible(true); }}>{v}</a>,
    },
    { title: '手机号', dataIndex: 'msisdn', key: 'msisdn', width: 140 },
    {
      title: '运营商', dataIndex: 'operator', key: 'operator', width: 100,
      render: (v: string) => <Tag>{operatorMap[v] || v}</Tag>,
    },
    {
      title: '状态', dataIndex: 'status', key: 'status', width: 90,
      render: (v: string) => <Badge status={statusMap[v]?.color as any} text={statusMap[v]?.text} />,
    },
    { title: '套餐', dataIndex: 'package', key: 'package', width: 120 },
    {
      title: '流量使用', key: 'traffic', width: 160,
      render: (_: any, r: any) => {
        const pct = r.totalMB ? Math.round((r.usedMB / r.totalMB) * 100) : 0;
        return (
          <div>
            <div style={{ fontSize: 11, color: '#666' }}>
              {(r.usedMB / 1024).toFixed(1)}GB / {(r.totalMB / 1024).toFixed(0)}GB
            </div>
            <Progress percent={pct} size="small"
              strokeColor={pct > 80 ? '#ff4d4f' : pct > 60 ? '#faad14' : '#52c41a'} showInfo={false} />
          </div>
        );
      },
    },
    { title: '归属客户', dataIndex: 'client', key: 'client', width: 140 },
    { title: '到期日',  dataIndex: 'expireDate', key: 'expireDate', width: 110 },
    {
      title: '操作', key: 'action', fixed: 'right' as const, width: 130,
      render: (_: any, r: any) => (
        <Space size={4}>
          {r.status === 'active'
            ? <Tooltip title="停机"><Button size="small" icon={<PauseCircleOutlined />} onClick={() => handleStop(r.iccid)} /></Tooltip>
            : <Tooltip title="复机"><Button size="small" icon={<PlayCircleOutlined />} type="primary" onClick={() => handleResume(r.iccid)} /></Tooltip>
          }
          <Tooltip title="改套餐"><Button size="small" icon={<EditOutlined />} onClick={() => message.info('套餐变更')} /></Tooltip>
          <Tooltip title="更多">
            <Button size="small" icon={<EllipsisOutlined />} onClick={() => { setCurrentCard(r); setDetailVisible(true); }} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      {/* API 状态提示 */}
      <Alert
        style={{ marginBottom: 12 }}
        type={IS_REAL_API ? 'success' : 'info'}
        showIcon
        icon={IS_REAL_API ? <CloudOutlined /> : <ApiOutlined />}
        message={IS_REAL_API ? '已连接跃创真实API' : '当前使用模拟数据'}
        description={
          IS_REAL_API
            ? `API地址: ${process.env.REACT_APP_YUE_API_URL}`
            : '在 .env.local 中配置 REACT_APP_YUE_APP_KEY / REACT_APP_YUE_SECRET 接入真实数据'
        }
        closable
      />

      <Card bordered={false} style={{ borderRadius: 8 }}>
        <Row gutter={12} style={{ marginBottom: 16 }}>
          <Col flex="280px">
            <Input placeholder="ICCID / 手机号 / 客户" prefix={<SearchOutlined />}
              value={search} onChange={e => setSearch(e.target.value)} allowClear
              onPressEnter={() => fetchCards()} />
          </Col>
          <Col flex="130px">
            <Select placeholder="卡状态" style={{ width: '100%' }} allowClear
              value={statusFilter || undefined} onChange={v => setStatusFilter(v || '')}
              options={[
                { value: 'active', label: '正常' }, { value: 'offline', label: '离线' },
                { value: 'stop',   label: '停机' }, { value: 'testing', label: '测试期' },
              ]} />
          </Col>
          <Col flex="130px">
            <Select placeholder="运营商" style={{ width: '100%' }} allowClear
              value={operatorFilter || undefined} onChange={v => setOperatorFilter(v || '')}
              options={[
                { value: 'cmcc', label: '中国移动' }, { value: 'cucc', label: '中国联通' },
                { value: 'ctcc', label: '中国电信' }, { value: 'global', label: '全球卡' },
              ]} />
          </Col>
          <Col flex="auto" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button icon={<ReloadOutlined />} onClick={() => fetchCards()}>刷新</Button>
            <Button icon={<UploadOutlined />}>导入</Button>
            <Button icon={<DownloadOutlined />}>导出</Button>
            {selectedKeys.length > 0 && (
              <Space>
                <Popconfirm title={`确认批量停机 ${selectedKeys.length} 张卡？`} onConfirm={() => handleBatch('stop')}>
                  <Button danger>批量停机 ({selectedKeys.length})</Button>
                </Popconfirm>
                <Button type="primary" onClick={() => handleBatch('resume')}>
                  批量复机 ({selectedKeys.length})
                </Button>
              </Space>
            )}
          </Col>
        </Row>

        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={data}
            scroll={{ x: 1300 }}
            rowSelection={{ selectedRowKeys: selectedKeys, onChange: keys => setSelectedKeys(keys as string[]) }}
            pagination={{
              ...pagination,
              showTotal: t => `共 ${t} 条`,
              showSizeChanger: true,
              onChange: (page, size) => fetchCards(page, size),
            }}
            size="small"
          />
        </Spin>
      </Card>

      {/* 详情弹窗 */}
      <Modal
        title={`卡片详情 — ${currentCard?.iccid}`}
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={640}
      >
        {currentCard && (
          <div>
            {[
              ['ICCID',   currentCard.iccid],
              ['MSISDN',  currentCard.msisdn],
              ['IMSI',    currentCard.imsi],
              ['运营商',  operatorMap[currentCard.operator] || currentCard.operator],
              ['状态',    statusMap[currentCard.status]?.text],
              ['套餐',    currentCard.package],
              ['归属客户',currentCard.client],
              ['激活日期',currentCard.activateDate],
              ['到期日期',currentCard.expireDate],
            ].map(([label, val]) => (
              <Row key={label as string} style={{ marginBottom: 10 }}>
                <Col span={6} style={{ color: '#999' }}>{label}</Col>
                <Col span={18} style={{ fontWeight: 500 }}>{val}</Col>
              </Row>
            ))}
            {currentCard.totalMB > 0 && (
              <div style={{ marginTop: 8 }}>
                <div style={{ color: '#999', marginBottom: 4 }}>流量使用</div>
                <Progress
                  percent={Math.round((currentCard.usedMB / currentCard.totalMB) * 100)}
                  strokeColor={{ '0%': '#108ee9', '100%': currentCard.usedMB / currentCard.totalMB > 0.8 ? '#ff4d4f' : '#52c41a' }}
                />
                <div style={{ fontSize: 12, color: '#666' }}>
                  已用 {(currentCard.usedMB / 1024).toFixed(2)} GB / 共 {(currentCard.totalMB / 1024).toFixed(0)} GB
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CardManage;
